import { KanbanColumn } from '@/task/components/KanbanColumn'
import { Box, Button, HStack } from '@chakra-ui/react'
import { DragDropContext, Draggable, DropResult } from '@hello-pangea/dnd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  ShareBoard,
  ShareColumn,
  SummaryPiece,
  editPiece,
  keepAllRemaining,
  movePiece,
  shareColumns,
} from '../meetingSummaryShareBoard'
import MeetingSummaryShareCard from './MeetingSummaryShareCard'

interface Props {
  pieces: SummaryPiece[]
  board: ShareBoard
  onChange(board: ShareBoard): void
}

const columnColors: Record<ShareColumn, string> = {
  todo: 'gray',
  keep: 'green',
  edit: 'orange',
  drop: 'red',
}

// Step 2: the four columns, with the drag and drop of the projects kanban
export default function MeetingSummaryShareBoard({
  pieces,
  board,
  onChange,
}: Props) {
  const { t } = useTranslation()
  const byId = new Map(pieces.map((piece) => [piece.id, piece]))

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    onChange(
      movePiece(
        board,
        pieces,
        result.draggableId,
        result.destination.droppableId as ShareColumn,
        result.destination.index
      )
    )
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      {/* On top, where the eye starts: the quick way when little is held back */}
      {board.columns.todo.length > 0 && (
        <Box mb={3}>
          <Button
            size="sm"
            colorScheme="green"
            variant="outline"
            onClick={() => onChange(keepAllRemaining(board))}
          >
            {t('MeetingSummaryShareModal.keepAll')}
          </Button>
        </Box>
      )}
      <HStack align="stretch" pb={5} minW="min-content">
        {shareColumns.map((column) => (
          <KanbanColumn
            key={column}
            id={column}
            title={t(`MeetingSummaryShareModal.columns.${column}`)}
            colorScheme={columnColors[column]}
            count={board.columns[column].length}
            stretch
          >
            {board.columns[column].map((id, index) => {
              const piece = byId.get(id)
              if (!piece) return null
              return (
                <Draggable key={id} draggableId={id} index={index}>
                  {(provided, snapshot) => (
                    <MeetingSummaryShareCard
                      ref={provided.innerRef}
                      piece={piece}
                      isDragging={snapshot.isDragging}
                      edited={column === 'edit' ? board.edited[id] : undefined}
                      onEdit={(body) => onChange(editPiece(board, id, body))}
                      mb={2}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    />
                  )}
                </Draggable>
              )
            })}
          </KanbanColumn>
        ))}
      </HStack>
    </DragDropContext>
  )
}
